<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { defaultOutfitFor, outfitFromParts, outfitsFor, relativeAge, validateDisplayName, WORLD, type AvatarType, type DrawingGameCanvasResetEvent, type DrawingGameNotice, type DrawingGameSnapshot, type DrawingGameStrokeEvent, type DrawingGameSummary, type DrawingStroke, type EmoteType, type LocalChatMessage, type MacaoNotice, type MacaoSnapshot, type MacaoSuit, type MacaoSummary, type MaintenanceNotice, type OutfitId } from "@aproape/shared";
import { createGuestSession, getRelationship, loginAccount, registerAccount, restoreSession, revokeSession, setRelationship, updateAvatarSkin, updateProfileName, type RelationshipKind, type SessionUser } from "./api";
import { DragGesture } from "./game/gesture";
import { GameWorld } from "./game/world";
import type { AvatarSnapshot } from "./game/avatar";
import { WorldNetwork } from "./network";
import { ChatSound, macaoSoundCues, shouldPlayChatSound } from "./sound";
import { minimapPosition } from "./minimap";
import { appendChatMessage } from "./chat-feed";
import DrawingGamePanel from "./DrawingGamePanel.vue";
import MacaoGamePanel from "./MacaoGamePanel.vue";

declare global { interface Window { __aproapeTest?: { tapAvatarByName: (name: string) => boolean; openDrawingTable: () => boolean; openMacaoTable: () => boolean; isAvatarTyping: (name: string) => boolean } } }

const loading = ref(true);
const entering = ref(false);
const error = ref("");
const authMode = ref<"register" | "login" | "guest">("login");
const name = ref("");
const email = ref("");
const password = ref("");
const showPassword = ref(false);
const avatarType = ref<AvatarType>("female");
const avatarOutfit = ref<OutfitId>("female_sport");
const session = ref<SessionUser | null>(null);
const worldHost = ref<HTMLElement>();
const connection = ref<"connecting" | "online" | "offline">("connecting");
const onlineCount = ref(0);
const minimapPlayers = ref<AvatarSnapshot[]>([]);
const messages = ref<LocalChatMessage[]>([]);
const chatText = ref("");
const selected = ref<AvatarSnapshot | null>(null);
const accountOpen = ref(false);
const emoteOpen = ref(false);
const skinPickerOpen = ref(false);
const savingSkin = ref(false);
const skinError = ref("");
const editingName = ref(false);
const profileName = ref("");
const savingName = ref(false);
const nameError = ref("");
const relationship = ref<RelationshipKind | null>(null);
const relationshipLoading = ref(false);
const relationshipSaving = ref(false);
const relationshipError = ref("");
const drawingOpen = ref(false);
const drawingSessions = ref<DrawingGameSummary[]>([]);
const drawingState = ref<DrawingGameSnapshot | null>(null);
const drawingSecret = ref("");
const drawingStrokeEvent = ref<DrawingGameStrokeEvent | null>(null);
const drawingCanvasResetEvent = ref<DrawingGameCanvasResetEvent | null>(null);
const drawingNotices = ref<DrawingGameNotice[]>([]);
const macaoOpen = ref(false);
const macaoSummary = ref<MacaoSummary | null>(null);
const macaoState = ref<MacaoSnapshot | null>(null);
const macaoNotice = ref<MacaoNotice | null>(null);
const profileNameInput = ref<HTMLInputElement>();
const maintenanceNotice = ref<MaintenanceNotice | null>(null);
const joystick = ref({ visible: false, x: 0, y: 0, knobX: 0, knobY: 0 });
const gesture = new DragGesture();
const chatSound = new ChatSound();
let activePointer: number | null = null;
const worldPointers = new Map<number, { x: number; y: number }>();
let pinchActive = false;
let pinchConsumed = false;
let pinchStartDistance = 0;
let pinchStartZoom = 1;
let chatTyping = false;
let chatTypingHeartbeatAt = 0;
let chatTypingTimer: number | undefined;
const selfSessionId = ref("");
let game: GameWorld | undefined;
let network: WorldNetwork | undefined;
let viewportFrame: number | undefined;
const viewportTimers = new Set<number>();
const tokenKey = "aproape_session_token";
const activeInstanceKey = "aproape_active_instance";
const browserInstanceId = crypto.randomUUID();
const emoteOptions: ReadonlyArray<{ id: EmoteType; icon: string; label: string }> = [
  { id: "dance", icon: "💃", label: "Dans" },
  { id: "stunt", icon: "🤸", label: "Cascadorie" },
  { id: "jump", icon: "✨", label: "Săritură" },
];
const relationshipOptions: ReadonlyArray<{ id: RelationshipKind; icon: string; label: string }> = [
  { id: "friend", icon: "☺", label: "Prieten" },
  { id: "boyfriend", icon: "♥", label: "Iubit" },
  { id: "girlfriend", icon: "♥", label: "Iubită" },
];
const outfitOptions = computed(() => outfitsFor(avatarType.value));
const outfitPositions: Record<OutfitId, string> = {
  female_sport: "0% 0%",
  female_noir: "50% 0%",
  female_blush: "100% 0%",
  male_urban: "0% 100%",
  male_sand: "50% 100%",
  male_varsity: "100% 100%",
};

watch(avatarType, (type) => { avatarOutfit.value = defaultOutfitFor(type); });

const emailIsValid = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim()));
const canEnter = computed(() => {
  if (entering.value) return false;
  const nameIsValid = Boolean(validateDisplayName(name.value));
  if (authMode.value === "guest") return nameIsValid;
  if (!emailIsValid.value || password.value.length < 8) return false;
  return authMode.value === "login" || nameIsValid;
});
const createdLabel = computed(() => selected.value ? `Personaj creat ${relativeAge(selected.value.createdAt)}` : "");
const exactCreated = computed(() => selected.value ? new Intl.DateTimeFormat("ro-RO", { day: "numeric", month: "long", year: "numeric" }).format(selected.value.createdAt) : "");
const isOwnProfile = computed(() => Boolean(selected.value && session.value && selected.value.userId === session.value.id));
const profileDialogLabel = computed(() => isOwnProfile.value ? "Profilul tău" : `Profilul lui ${selected.value?.displayName ?? "utilizator"}`);
const relationshipStatus = computed(() => relationship.value === "friend" ? "Este prietenul tău" : relationship.value === "boyfriend" ? "Este iubitul tău" : relationship.value === "girlfriend" ? "Este iubita ta" : "Alege cum vă cunoașteți");
const currentDrawingPlayer = computed(() => drawingState.value?.players.find((player) => player?.userId === session.value?.id) ?? null);
const isDrawingGuessMode = computed(() => Boolean(drawingState.value?.viewerRole === "player" && drawingState.value.session.status === "drawing" && currentDrawingPlayer.value && !currentDrawingPlayer.value.isDrawer && !currentDrawingPlayer.value.guessed));
const chatPlaceholder = computed(() => isDrawingGuessMode.value ? "Scrie răspunsul pentru joc…" : "Scrie ceva tuturor din lume…");
const chatInputLabel = computed(() => isDrawingGuessMode.value ? "Răspuns pentru cuvânt" : "Mesaj public pentru toată lumea");
const chatSubmitLabel = computed(() => isDrawingGuessMode.value ? "Trimite răspunsul" : "Trimite mesajul");
const visibleChatItems = computed(() => {
  const worldItems = messages.value.map((message) => ({ id: message.id, displayName: message.displayName, text: message.text, at: message.sentAt, game: false }));
  if (drawingState.value?.viewerRole !== "player") return worldItems.slice(-6);
  const gameItemsByEvent = new Map<string, { id: string; displayName: string; text: string; at: number; game: boolean }>();
  for (const item of drawingState.value.feed) gameItemsByEvent.set(`${item.at}-${item.text}`, { id: item.id, displayName: "Joc", text: item.text, at: item.at, game: true });
  for (const notice of drawingNotices.value) {
    if (notice.sessionId !== drawingState.value.session.id) continue;
    if (["guess_submitted", "player_guessed", "round_started", "match_ended"].includes(notice.type)) continue;
    gameItemsByEvent.set(`${notice.at}-${notice.text}`, { id: `${notice.sessionId}-${notice.at}-${notice.type}`, displayName: "Joc", text: notice.text, at: notice.at, game: true });
  }
  const gameItems = [...gameItemsByEvent.values()];
  return [...worldItems, ...gameItems].sort((left, right) => left.at - right.at).slice(-6);
});

watch(isDrawingGuessMode, () => { stopChatTyping(); chatText.value = ""; });

onMounted(async () => {
  localStorage.removeItem("aproape_guest_token");
  const token = readSessionToken();
  if (token) {
    try { session.value = await restoreSession(token); if (!session.value) clearSessionToken(); }
    catch { clearSessionToken(); }
  }
  loading.value = false;
  if (session.value && token) { await nextTick(); await startWorld(token); }
  window.addEventListener("blur", stopMovement);
  window.addEventListener("pointerdown", unlockChatSound, { passive: true });
  window.addEventListener("keydown", unlockChatSound);
  document.addEventListener("visibilitychange", handleVisibility);
  window.addEventListener("resize", scheduleViewportSync);
  window.addEventListener("orientationchange", scheduleViewportSync);
  window.visualViewport?.addEventListener("resize", scheduleViewportSync);
  window.visualViewport?.addEventListener("scroll", scheduleViewportSync);
  scheduleViewportSync();
});

onBeforeUnmount(() => {
  stopMovement(); stopChatTyping(); network?.destroy(false); game?.destroy(); chatSound.destroy();
  window.removeEventListener("blur", stopMovement); document.removeEventListener("visibilitychange", handleVisibility);
  window.removeEventListener("pointerdown", unlockChatSound); window.removeEventListener("keydown", unlockChatSound);
  window.removeEventListener("resize", scheduleViewportSync);
  window.removeEventListener("orientationchange", scheduleViewportSync);
  window.visualViewport?.removeEventListener("resize", scheduleViewportSync);
  window.visualViewport?.removeEventListener("scroll", scheduleViewportSync);
  if (viewportFrame) cancelAnimationFrame(viewportFrame);
  for (const timer of viewportTimers) clearTimeout(timer);
  document.body.classList.remove("world-active");
});

function updateViewportMetrics() {
  const viewport = window.visualViewport;
  const height = Math.max(1, Math.round(viewport?.height ?? window.innerHeight));
  const top = Math.max(0, Math.round(viewport?.offsetTop ?? 0));
  document.documentElement.style.setProperty("--app-height", `${height}px`);
  document.documentElement.style.setProperty("--app-top", `${top}px`);
  game?.resize();
}
function scheduleViewportSync() {
  if (viewportFrame) cancelAnimationFrame(viewportFrame);
  for (const timer of viewportTimers) clearTimeout(timer);
  viewportTimers.clear();
  viewportFrame = requestAnimationFrame(() => {
    viewportFrame = undefined;
    updateViewportMetrics();
  });
  for (const delay of [80, 240]) {
    const timer = window.setTimeout(() => {
      viewportTimers.delete(timer);
      updateViewportMetrics();
    }, delay);
    viewportTimers.add(timer);
  }
}
function handleVisibility() {
  if (document.hidden) {
    stopMovement();
    stopChatTyping();
    network?.presence(true);
    return;
  }
  network?.presence(false);
  network?.ensureConnected();
}
function unlockChatSound() { void chatSound.unlock(); }
function minimapPlayerStyle(player: AvatarSnapshot) {
  const position = minimapPosition(player.x, player.y, WORLD.width, WORLD.height);
  return { left: `${position.left}%`, top: `${position.top}%` };
}
function readSessionToken(): string | null { return sessionStorage.getItem(tokenKey) ?? localStorage.getItem(tokenKey); }
function clearSessionToken(): void { sessionStorage.removeItem(tokenKey); localStorage.removeItem(tokenKey); }
function switchAuthMode(mode: "register" | "login" | "guest") {
  authMode.value = mode;
  error.value = "";
  showPassword.value = false;
}

async function enterWorld() {
  if (!canEnter.value) return;
  entering.value = true; error.value = "";
  try {
    const authenticated = authMode.value === "register"
      ? await registerAccount(name.value, email.value, password.value, avatarType.value, avatarOutfit.value)
      : authMode.value === "guest"
        ? await createGuestSession(name.value, avatarType.value, avatarOutfit.value)
        : await loginAccount(email.value, password.value);
    if (authenticated.user.isGuest) {
      localStorage.removeItem(tokenKey);
      sessionStorage.setItem(tokenKey, authenticated.token);
    } else {
      sessionStorage.removeItem(tokenKey);
      localStorage.setItem(tokenKey, authenticated.token);
    }
    session.value = authenticated.user;
    password.value = "";
    await startWorld(authenticated.token);
  } catch (caught) { error.value = caught instanceof Error ? caught.message : "A apărut o problemă."; }
  finally { entering.value = false; }
}

async function startWorld(token: string) {
  (document.activeElement as HTMLElement | null)?.blur();
  document.body.classList.add("world-active");
  updateViewportMetrics();
  await nextTick();
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  if (!worldHost.value) await nextTick();
  if (!worldHost.value) return;
  game = new GameWorld(worldHost.value, { onAvatarTap: (avatar) => void selectAvatar(avatar), onDrawingTableTap: openDrawingTable, onMacaoTableTap: openMacaoTable });
  await game.init();
  localStorage.setItem(activeInstanceKey, browserInstanceId);
  network = new WorldNetwork(token, {
    onPlayers: (players) => {
      onlineCount.value = players.length;
      minimapPlayers.value = players;
      game?.syncPlayers(players);
      if (import.meta.env.DEV) window.__aproapeTest = { tapAvatarByName: (targetName) => game?.tapAvatarByName(targetName) ?? false, openDrawingTable: () => game?.tapDrawingTable() ?? false, openMacaoTable: () => game?.tapMacaoTable() ?? false, isAvatarTyping: (targetName) => game?.isAvatarTyping(targetName) ?? false };
    },
    onChat: (message) => {
      if (shouldPlayChatSound(message.senderId, selfSessionId.value)) chatSound.playChat();
      game?.showChat(message);
      messages.value = appendChatMessage(messages.value, message);
    },
    onTyping: (status) => {
      game?.showTyping(status.senderId, status.active);
      if (status.active && status.senderId !== selfSessionId.value) chatSound.playTyping();
    },
    onMaintenance: (notice) => {
      stopMovement();
      accountOpen.value = false;
      emoteOpen.value = false;
      skinPickerOpen.value = false;
      selected.value = null;
      drawingOpen.value = false;
      macaoOpen.value = false;
      maintenanceNotice.value = notice;
    },
    onStatus: (status) => {
      connection.value = status;
      if (status === "online" && maintenanceNotice.value) {
        window.location.reload();
        return;
      }
      if (status === "online") maintenanceNotice.value = null;
    },
    onSession: (id) => { selfSessionId.value = id; game?.setSelf(id); },
    onSessionReplaced: handleSessionReplaced,
    onDrawingSessions: (sessions) => {
      drawingSessions.value = sessions;
      game?.setDrawingTableStatus(sessions[0]);
      if (drawingOpen.value && !drawingState.value && sessions[0]) network?.inspectDrawingGame(sessions[0].id);
    },
    onDrawingState: (state) => {
      const previousState = drawingState.value;
      if (drawingOpen.value && previousState?.session.id === state.session.id && state.session.playerCount > previousState.session.playerCount) chatSound.playUi();
      drawingState.value = state;
      if (state.drawerUserId !== session.value?.id || state.session.status !== "drawing") drawingSecret.value = "";
      if (state.viewerRole !== "none") drawingOpen.value = true;
    },
    onDrawingSecret: (secret) => {
      if (secret.sessionId === drawingState.value?.session.id || !drawingState.value) drawingSecret.value = secret.word;
    },
    onDrawingStroke: (event) => {
      if (drawingOpen.value && event.sessionId === drawingState.value?.session.id) chatSound.playDrawing();
      drawingStrokeEvent.value = { ...event, stroke: { ...event.stroke, points: [...event.stroke.points] } };
    },
    onDrawingCanvasReset: (event) => {
      if (drawingOpen.value && event.sessionId === drawingState.value?.session.id) chatSound.playUi();
      drawingCanvasResetEvent.value = { ...event, strokes: event.strokes.map((stroke) => ({ ...stroke, points: [...stroke.points] })) };
    },
    onDrawingNotice: (notice) => {
      if (drawingOpen.value && notice.sessionId === drawingState.value?.session.id) chatSound.playDrawingNotice(notice.type);
      drawingNotices.value = [...drawingNotices.value, notice].slice(-12);
    },
    onDrawingGuessResult: () => {},
    onMacaoSummary: (summary) => { macaoSummary.value = summary; game?.setMacaoTableStatus(summary); },
    onMacaoState: (state) => {
      for (const cue of macaoSoundCues(macaoState.value, state, session.value?.id ?? "")) chatSound.playMacao(cue);
      macaoState.value = state;
      if (state.viewerRole === "player") macaoOpen.value = true;
    },
    onMacaoNotice: (notice) => { macaoNotice.value = notice; chatSound.playMacao(notice.type === "error" ? "invalidCard" : notice.type === "win" ? "macaoWin" : "turn"); },
  });
  network.presence(document.hidden);
  await network.connect();
  scheduleViewportSync();
}

function pointerDown(event: PointerEvent) {
  if (maintenanceNotice.value) return;
  if (event.pointerType === "mouse" && event.button !== 0) return;
  emoteOpen.value = false;
  worldPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
  (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  if (event.pointerType !== "mouse" && worldPointers.size >= 2) {
    const [first, second] = [...worldPointers.values()];
    pinchStartDistance = Math.hypot(second!.x - first!.x, second!.y - first!.y);
    pinchStartZoom = game?.getZoom() ?? 1;
    pinchActive = true;
    pinchConsumed = true;
    stopMovement();
    return;
  }
  activePointer = event.pointerId;
  gesture.begin({ x: event.clientX, y: event.clientY });
}
function pointerMove(event: PointerEvent) {
  if (maintenanceNotice.value) return;
  if (worldPointers.has(event.pointerId)) worldPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
  if (pinchActive && worldPointers.size >= 2) {
    event.preventDefault();
    const [first, second] = [...worldPointers.values()];
    const distance = Math.hypot(second!.x - first!.x, second!.y - first!.y);
    game?.zoomWithPinch(pinchStartZoom, pinchStartDistance, distance, (first!.x + second!.x) / 2, (first!.y + second!.y) / 2);
    return;
  }
  if (pinchConsumed) return;
  if (event.pointerId !== activePointer) return;
  const state = gesture.move({ x: event.clientX, y: event.clientY });
  if (!state?.dragging) return;
  joystick.value = { visible: true, x: state.origin.x, y: state.origin.y, knobX: state.vector.x * gesture.radius, knobY: state.vector.y * gesture.radius };
  game?.setInput(state.vector.x, state.vector.y); network?.movement(state.vector.x, state.vector.y);
}
function pointerUp(event: PointerEvent) {
  const wasPinch = pinchActive || pinchConsumed;
  worldPointers.delete(event.pointerId);
  if (worldPointers.size < 2) pinchActive = false;
  if (worldPointers.size === 0) pinchConsumed = false;
  if (wasPinch) { stopMovement(); return; }
  if (event.pointerId !== activePointer) return;
  const result = gesture.end({ x: event.clientX, y: event.clientY });
  if (!result.wasDrag) game?.tapAt(result.point.x, result.point.y);
  stopMovement();
}
function stopMovement() {
  if (joystick.value.visible || activePointer !== null) network?.movement(0, 0);
  game?.setInput(0, 0); gesture.cancel(); activePointer = null; joystick.value.visible = false;
}
function wheelZoom(event: WheelEvent) {
  if (maintenanceNotice.value) return;
  game?.zoomWithWheel(event.deltaY, event.clientX, event.clientY);
}
function openDrawingTable() {
  chatSound.playUi();
  stopMovement();
  selected.value = null;
  accountOpen.value = false;
  emoteOpen.value = false;
  macaoOpen.value = false;
  drawingOpen.value = true;
  const preferred = drawingState.value?.session.id ?? drawingSessions.value[0]?.id;
  if (preferred) network?.inspectDrawingGame(preferred);
}
function closeDrawingTable() { drawingOpen.value = false; }
function resetDrawingGameUi() { drawingOpen.value = false; drawingSessions.value = []; drawingState.value = null; drawingSecret.value = ""; drawingStrokeEvent.value = null; drawingCanvasResetEvent.value = null; drawingNotices.value = []; }
function resetMacaoUi() { macaoOpen.value = false; macaoSummary.value = null; macaoState.value = null; macaoNotice.value = null; }
function inspectDrawingGame(sessionId: string) { drawingSecret.value = ""; network?.inspectDrawingGame(sessionId); }
function joinDrawingGame(sessionId: string) { chatSound.playUi(); network?.joinDrawingGame(sessionId); }
function startDrawingGame(sessionId: string) { chatSound.playUi(); network?.startDrawingGame(sessionId); }
function watchDrawingGame(sessionId: string) { chatSound.playUi(); network?.watchDrawingGame(sessionId); }
function queueDrawingGame(sessionId: string) { chatSound.playUi(); network?.queueDrawingGame(sessionId); }
function createDrawingGame() { chatSound.playUi(); network?.createDrawingGame(); }
function leaveDrawingGame(sessionId: string) { chatSound.playUi(); drawingSecret.value = ""; network?.leaveDrawingGame(sessionId); }
function replayDrawingGame(sessionId: string) { drawingSecret.value = ""; network?.replayDrawingGame(sessionId); }
function remainDrawingSpectator(sessionId: string) { drawingSecret.value = ""; network?.remainDrawingSpectator(sessionId); }
function sendDrawingStroke(sessionId: string, stroke: DrawingStroke) { chatSound.playDrawing(); network?.sendDrawingStroke(sessionId, stroke); }
function clearDrawingCanvas(sessionId: string) { network?.clearDrawingCanvas(sessionId); }
function undoDrawingCanvas(sessionId: string) { network?.undoDrawingCanvas(sessionId); }
function openMacaoTable() {
  chatSound.playUi(); stopMovement(); selected.value = null; accountOpen.value = false; emoteOpen.value = false; drawingOpen.value = false; macaoOpen.value = true; network?.inspectMacao();
}
function closeMacaoTable() { macaoOpen.value = false; }
function joinMacao() { macaoNotice.value = null; chatSound.playUi(); network?.joinMacao(); }
function startMacao() { macaoNotice.value = null; chatSound.playUi(); network?.startMacao(); }
function leaveMacao() { macaoNotice.value = null; chatSound.playUi(); network?.leaveMacao(); }
function playMacaoCard(cardId: string, chosenSuit?: MacaoSuit) { macaoNotice.value = null; chatSound.playUi(); network?.playMacaoCard(cardId, chosenSuit); }
function drawMacaoCard() { macaoNotice.value = null; chatSound.playUi(); network?.drawMacaoCard(); }
function replayMacao() { macaoNotice.value = null; chatSound.playUi(); network?.replayMacao(); }
function sendChat() {
  if (maintenanceNotice.value) return;
  const text = chatText.value.replace(/\s+/g, " ").trim();
  if (!text) return;
  stopChatTyping();
  if (isDrawingGuessMode.value && drawingState.value) {
    chatSound.playUi();
    network?.submitDrawingGuess(drawingState.value.session.id, text.slice(0, 50));
    chatText.value = "";
    return;
  }
  chatSound.playUi(); network?.chat(text.slice(0, WORLD.chatMaxLength)); chatText.value = "";
}
function sendEmote(emote: EmoteType) {
  if (maintenanceNotice.value) return;
  stopMovement();
  chatSound.playUi();
  network?.emote(emote);
  emoteOpen.value = false;
}
function focusChat() { stopMovement(); scheduleViewportSync(); }
function handleChatTyping(event: Event) {
  const value = (event.target as HTMLInputElement).value;
  if (isDrawingGuessMode.value) { stopChatTyping(); return; }
  if (!value.trim()) { stopChatTyping(); return; }
  const now = Date.now();
  if (!chatTyping || now - chatTypingHeartbeatAt >= 1_500) {
    network?.typing(true);
    chatTypingHeartbeatAt = now;
  }
  chatTyping = true;
  clearTimeout(chatTypingTimer);
  chatTypingTimer = window.setTimeout(stopChatTyping, 1_800);
}
function stopChatTyping() {
  clearTimeout(chatTypingTimer);
  chatTypingTimer = undefined;
  if (chatTyping) network?.typing(false);
  chatTyping = false;
  chatTypingHeartbeatAt = 0;
}
function blurChat() { stopChatTyping(); scheduleViewportSync(); }
function resetRelationshipPanel() { relationship.value = null; relationshipLoading.value = false; relationshipSaving.value = false; relationshipError.value = ""; }
async function selectAvatar(avatar: AvatarSnapshot) {
  selected.value = { ...avatar };
  resetRelationshipPanel();
  accountOpen.value = false;
  emoteOpen.value = false;
  if (!session.value || avatar.userId === session.value.id || session.value.isGuest || avatar.userId.startsWith("guest:")) return;
  const token = readSessionToken();
  if (!token) return;
  const targetUserId = avatar.userId;
  relationshipLoading.value = true;
  try {
    const current = await getRelationship(token, targetUserId);
    if (selected.value?.userId === targetUserId) relationship.value = current;
  } catch (caught) {
    if (selected.value?.userId === targetUserId) relationshipError.value = caught instanceof Error ? caught.message : "Nu am putut încărca relația.";
  } finally {
    if (selected.value?.userId === targetUserId) relationshipLoading.value = false;
  }
}
async function chooseRelationship(kind: RelationshipKind | null) {
  if (!selected.value || !session.value || relationshipSaving.value) return;
  const token = readSessionToken();
  if (!token) return;
  const targetUserId = selected.value.userId;
  relationshipSaving.value = true;
  relationshipError.value = "";
  try {
    const saved = await setRelationship(token, targetUserId, kind);
    if (selected.value?.userId === targetUserId) relationship.value = saved;
  } catch (caught) {
    if (selected.value?.userId === targetUserId) relationshipError.value = caught instanceof Error ? caught.message : "Nu am putut salva relația.";
  } finally {
    if (selected.value?.userId === targetUserId) relationshipSaving.value = false;
  }
}
function closeSheet() { selected.value = null; skinPickerOpen.value = false; editingName.value = false; nameError.value = ""; resetRelationshipPanel(); }
function outfitPreviewStyle(outfit: OutfitId) { return { backgroundPosition: outfitPositions[outfit] }; }
function openMyProfile() {
  if (!session.value) return;
  selected.value = {
    id: session.value.id,
    userId: session.value.id,
    displayName: session.value.displayName,
    avatarType: session.value.avatarType,
    avatarParts: { ...session.value.avatarParts },
    createdAt: session.value.createdAt,
    x: 0,
    y: 0,
    direction: "down",
    moving: false,
    away: false,
    lounging: false,
    loungeRotation: 0,
    emote: "",
    emoteUntil: 0,
  };
  accountOpen.value = false;
  emoteOpen.value = false;
  skinPickerOpen.value = false;
  editingName.value = false;
  nameError.value = "";
  resetRelationshipPanel();
}
async function beginNameEdit() {
  if (!session.value) return;
  profileName.value = session.value.displayName;
  nameError.value = "";
  editingName.value = true;
  await nextTick();
  profileNameInput.value?.focus();
  profileNameInput.value?.select();
}
function cancelNameEdit() { editingName.value = false; nameError.value = ""; }
async function saveProfileName() {
  if (!session.value || savingName.value) return;
  const displayName = validateDisplayName(profileName.value);
  if (!displayName) { nameError.value = "Numele trebuie să aibă între 3 și 20 de caractere."; return; }
  if (displayName === session.value.displayName) { editingName.value = false; return; }
  const token = readSessionToken();
  if (!token) return;
  savingName.value = true;
  nameError.value = "";
  try {
    const updated = await updateProfileName(token, displayName);
    session.value = updated;
    if (selected.value) selected.value = { ...selected.value, displayName: updated.displayName };
    network?.displayName(updated.displayName);
    editingName.value = false;
  } catch (caught) {
    nameError.value = caught instanceof Error ? caught.message : "Nu am putut salva numele.";
  } finally {
    savingName.value = false;
  }
}
function openSkinPicker() {
  openMyProfile();
  skinPickerOpen.value = true;
  skinError.value = "";
}
async function chooseSkin(outfit: OutfitId) {
  if (!session.value || savingSkin.value || outfitFromParts(session.value.avatarType, session.value.avatarParts) === outfit) return;
  const token = readSessionToken();
  if (!token) return;
  savingSkin.value = true;
  skinError.value = "";
  try {
    const updated = await updateAvatarSkin(token, outfit);
    session.value = updated;
    if (selected.value) selected.value = { ...selected.value, avatarParts: { ...updated.avatarParts } };
    network?.appearance(outfit);
  } catch (caught) {
    skinError.value = caught instanceof Error ? caught.message : "Nu am putut salva ținuta.";
  } finally {
    savingSkin.value = false;
  }
}
async function logout() {
  stopMovement();
  stopChatTyping();
  network?.destroy(true);
  game?.destroy();
  network = undefined;
  game = undefined;
  const token = readSessionToken();
  clearSessionToken();
  if (localStorage.getItem(activeInstanceKey) === browserInstanceId) localStorage.removeItem(activeInstanceKey);
  messages.value = [];
  minimapPlayers.value = [];
  onlineCount.value = 0;
  chatText.value = "";
  selected.value = null;
  resetDrawingGameUi();
  resetMacaoUi();
  accountOpen.value = false;
  emoteOpen.value = false;
  skinPickerOpen.value = false;
  connection.value = "connecting";
  maintenanceNotice.value = null;
  selfSessionId.value = "";
  session.value = null;
  document.body.classList.remove("world-active");
  authMode.value = "login";
  password.value = "";
  if (token) await revokeSession(token);
}

function handleSessionReplaced() {
  stopMovement();
  stopChatTyping();
  network?.destroy(false);
  game?.destroy();
  network = undefined;
  game = undefined;
  sessionStorage.removeItem(tokenKey);
  if (localStorage.getItem(activeInstanceKey) === browserInstanceId) {
    localStorage.removeItem(tokenKey);
    localStorage.removeItem(activeInstanceKey);
  }
  messages.value = [];
  minimapPlayers.value = [];
  onlineCount.value = 0;
  chatText.value = "";
  selected.value = null;
  resetDrawingGameUi();
  resetMacaoUi();
  accountOpen.value = false;
  emoteOpen.value = false;
  skinPickerOpen.value = false;
  connection.value = "connecting";
  maintenanceNotice.value = null;
  selfSessionId.value = "";
  session.value = null;
  document.body.classList.remove("world-active");
  authMode.value = "login";
  password.value = "";
}
</script>

<template>
  <main v-if="loading" class="loading-screen" aria-live="polite">
    <span class="pulse-logo">a</span><p>Pregătim piațeta…</p>
  </main>

  <main v-else-if="!session" class="onboarding">
    <section class="welcome-card">
      <div class="brand"><span class="brand-dot">a</span><span>aproape</span></div>
      <div class="intro-copy">
        <h1>O conversație bună începe <em>aproape.</em></h1>
        <aside class="world-preview world-preview-mobile" aria-label="Avanpremieră din lumea Aproape">
          <img src="/assets/world-preview.png" alt="Două personaje întâlnindu-se în lumea socială Aproape" />
        </aside>
        <p>{{ authMode === 'register' ? 'Creează-ți avatarul și intră în piațetă.' : authMode === 'guest' ? 'Alege un avatar temporar și intră imediat.' : 'Bine ai revenit. Conectează-te la avatarul tău.' }}</p>
      </div>
      <form class="entry-form" @submit.prevent="enterWorld">
        <div class="auth-switch" role="group" aria-label="Alege tipul autentificării">
          <button type="button" :class="{ active: authMode === 'register' }" @click="switchAuthMode('register')">Cont nou</button>
          <button type="button" :class="{ active: authMode === 'login' }" @click="switchAuthMode('login')">Am deja cont</button>
          <button type="button" :class="{ active: authMode === 'guest' }" @click="switchAuthMode('guest')">Guest</button>
        </div>
        <div v-if="authMode !== 'login'" class="form-field">
          <label for="display-name">Nume avatar</label>
          <input id="display-name" v-model="name" maxlength="20" minlength="3" autocomplete="nickname" placeholder="Cum să te vadă ceilalți" required />
        </div>
        <div v-if="authMode !== 'guest'" class="form-field">
          <label for="email">Email</label>
          <input id="email" v-model="email" type="email" maxlength="254" autocomplete="email" inputmode="email" placeholder="tu@exemplu.ro" required />
        </div>
        <div v-if="authMode !== 'guest'" class="form-field">
          <label for="password">Parolă</label>
          <div class="password-field">
            <input id="password" v-model="password" :type="showPassword ? 'text' : 'password'" minlength="8" maxlength="128" :autocomplete="authMode === 'login' ? 'current-password' : 'new-password'" placeholder="Minimum 8 caractere" required />
            <button type="button" :aria-label="showPassword ? 'Ascunde parola' : 'Arată parola'" @click="showPassword = !showPassword">{{ showPassword ? 'Ascunde' : 'Arată' }}</button>
          </div>
        </div>
        <fieldset v-if="authMode !== 'login'"><legend>Alege avatarul</legend><div class="avatar-choices">
          <button type="button" :class="['avatar-card', { selected: avatarType === 'female' }]" @click="avatarType = 'female'" :aria-pressed="avatarType === 'female'">
            <span class="avatar-crop"><img src="/assets/avatars/references/female.png" alt="Avatar feminin chibi" /></span><strong>Feminin</strong><span class="choice-check">✓</span>
          </button>
          <button type="button" :class="['avatar-card', { selected: avatarType === 'male' }]" @click="avatarType = 'male'" :aria-pressed="avatarType === 'male'">
            <span class="avatar-crop"><img src="/assets/avatars/references/male.png" alt="Avatar masculin chibi" /></span><strong>Masculin</strong><span class="choice-check">✓</span>
          </button>
        </div>
          <div class="onboarding-skins" role="radiogroup" aria-label="Alege ținuta">
            <button v-for="outfit in outfitOptions" :key="outfit.id" type="button" :class="['skin-choice', { selected: avatarOutfit === outfit.id }]" role="radio" :aria-checked="avatarOutfit === outfit.id" @click="avatarOutfit = outfit.id">
              <span class="outfit-preview" :style="outfitPreviewStyle(outfit.id)"></span><small>{{ outfit.label }}</small>
            </button>
          </div>
        </fieldset>
        <p v-if="error" class="form-error" role="alert">{{ error }}</p>
        <button class="enter-button" :disabled="!canEnter">{{ entering ? 'Intrăm…' : authMode === 'register' ? 'Creează contul' : authMode === 'guest' ? 'Intră ca Guest' : 'Conectează-te' }} <span>→</span></button>
        <p class="privacy-note">{{ authMode === 'guest' ? 'Avatarul Guest este temporar și rămâne doar în acest tab.' : 'Parola este protejată și nu este păstrată în clar.' }}</p>
      </form>
    </section>
    <aside class="world-preview world-preview-desktop" aria-label="Avanpremieră din lumea Aproape">
      <img src="/assets/world-preview.png" alt="Două personaje întâlnindu-se în lumea socială Aproape" />
      <div class="world-preview-copy"><p>ÎNĂUNTRU TE AȘTEAPTĂ</p><h2>O lume mică, plină de oameni aproape.</h2></div>
    </aside>
    <div class="orb orb-one"></div><div class="orb orb-two"></div>
  </main>

  <main v-else class="world-shell">
    <header class="world-header">
      <div class="mini-brand"><span>a</span> aproape</div>
      <div class="account-area">
        <div :class="['connection-pill', connection]" aria-live="polite" :aria-label="connection === 'online' ? `${onlineCount} persoane conectate` : connection === 'connecting' ? 'Se reconectează' : 'Conexiune offline'"><i></i>{{ connection === 'online' ? `${onlineCount} online` : connection === 'connecting' ? 'Reconectare…' : 'Offline' }}</div>
        <button class="account-trigger" type="button" :aria-expanded="accountOpen" aria-haspopup="menu" aria-label="Deschide meniul contului" @click="accountOpen = !accountOpen">
          <span :class="['account-avatar', session.avatarType]">{{ session.displayName.charAt(0).toUpperCase() }}</span><span class="account-chevron">⌄</span>
        </button>
        <div v-if="accountOpen" class="account-menu" role="menu">
          <p>{{ session.displayName }} · {{ session.isGuest ? 'Guest temporar' : session.email }}</p>
          <button type="button" role="menuitem" @click="openMyProfile"><span>☺</span> Profilul meu</button>
          <button type="button" role="menuitem" @click="openSkinPicker"><span>✦</span> Ținutele mele</button>
          <button type="button" role="menuitem" class="logout-item" @click="logout"><span>↪</span> Deconectare</button>
        </div>
        <aside class="minimap" :aria-label="`Minimapă cu ${onlineCount} persoane`">
          <div class="minimap-surface">
            <span v-for="player in minimapPlayers" :key="player.id" :class="['minimap-player', { self: player.id === selfSessionId, away: player.away }]" :style="minimapPlayerStyle(player)" :title="player.displayName" aria-hidden="true"></span>
          </div>
        </aside>
      </div>
    </header>
    <button v-if="accountOpen" class="menu-dismiss" type="button" aria-label="Închide meniul contului" @click="accountOpen = false"></button>
    <section ref="worldHost" class="world-canvas" aria-label="Lume socială 2D interactivă" tabindex="0"
      @pointerdown="pointerDown" @pointermove="pointerMove" @pointerup="pointerUp" @pointercancel="pointerUp" @wheel.prevent="wheelZoom" @contextmenu.prevent>
    </section>
    <div v-if="joystick.visible" class="joystick" :style="{ left: `${joystick.x}px`, top: `${joystick.y}px` }" aria-hidden="true"><i :style="{ transform: `translate(${joystick.knobX}px, ${joystick.knobY}px)` }"></i></div>

    <section :class="['chat-overlay', { 'game-active': drawingOpen || macaoOpen }]" @pointerdown.stop>
      <div class="chat-feed" aria-live="polite">
        <p v-if="visibleChatItems.length === 0" class="chat-hint"><span>☀</span> {{ isDrawingGuessMode ? 'Scrie aici răspunsul pentru joc.' : 'Mesajele ajung acum în toată lumea.' }}</p>
        <p v-for="(message, index) in visibleChatItems" :key="message.id" :class="['chat-line', { 'game-chat-line': message.game }]" :style="{ opacity: Math.max(.42, .55 + index / Math.max(visibleChatItems.length, 1) * .45) }"><strong>{{ message.displayName }}</strong> {{ message.text }}</p>
      </div>
      <div v-if="!drawingOpen && !macaoOpen && drawingState?.viewerRole !== 'player' && drawingState?.viewerRole !== 'spectator'" class="emote-dock" @pointerdown.stop>
        <Transition name="emote-pop">
          <div v-if="emoteOpen" class="emote-options" role="menu" aria-label="Alege un emote">
            <button v-for="option in emoteOptions" :key="option.id" type="button" class="emote-option" role="menuitem" :aria-label="option.label" :title="option.label" @click="sendEmote(option.id)">{{ option.icon }}</button>
          </div>
        </Transition>
        <button type="button" :class="['emote-trigger', { active: emoteOpen }]" :aria-expanded="emoteOpen" aria-haspopup="menu" aria-label="Deschide meniul de emote" @click="emoteOpen = !emoteOpen">☺</button>
      </div>
      <form class="chat-form" @submit.prevent="sendChat">
        <input v-model="chatText" :maxlength="isDrawingGuessMode ? 50 : WORLD.chatMaxLength" :placeholder="chatPlaceholder" :aria-label="chatInputLabel" @focus="focusChat" @input="handleChatTyping" @blur="blurChat" />
        <button :aria-label="chatSubmitLabel" :disabled="!chatText.trim()">↑</button>
      </form>
    </section>

    <DrawingGamePanel v-if="drawingOpen" :current-user-id="session.id" :sessions="drawingSessions" :state="drawingState" :secret="drawingSecret" :stroke-event="drawingStrokeEvent" :canvas-reset-event="drawingCanvasResetEvent"
      @close="closeDrawingTable" @inspect="inspectDrawingGame" @join="joinDrawingGame" @start="startDrawingGame" @watch="watchDrawingGame" @queue="queueDrawingGame" @create="createDrawingGame" @leave="leaveDrawingGame" @replay="replayDrawingGame" @remain-spectator="remainDrawingSpectator" @stroke="sendDrawingStroke" @clear="clearDrawingCanvas" @undo="undoDrawingCanvas" />

    <MacaoGamePanel v-if="macaoOpen" :current-user-id="session.id" :summary="macaoSummary" :state="macaoState" :notice="macaoNotice"
      @close="closeMacaoTable" @join="joinMacao" @start="startMacao" @leave="leaveMacao" @draw="drawMacaoCard" @play="playMacaoCard" @replay="replayMacao" />

    <div v-if="selected" class="sheet-backdrop" @click.self="closeSheet" @pointerdown.stop>
      <aside :class="['profile-sheet', { 'skin-editor-open': isOwnProfile && skinPickerOpen }]" role="dialog" aria-modal="true" :aria-label="profileDialogLabel">
        <span class="sheet-handle"></span><button class="sheet-close" @click="closeSheet" aria-label="Închide">×</button>
        <div :class="['mini-avatar', selected.avatarType]"><span class="hair"></span><span class="face">•‿•</span><span class="shirt"></span></div>
        <div class="profile-copy"><p class="profile-kicker">{{ isOwnProfile ? 'PROFILUL TĂU' : 'E LÂNGĂ TINE' }}</p>
          <div v-if="!editingName" class="profile-name-row"><h2>{{ selected.displayName }}</h2><button v-if="isOwnProfile" class="name-edit-button" type="button" aria-label="Editează numele avatarului" @click="beginNameEdit">✎</button></div>
          <form v-else class="profile-name-form" @submit.prevent="saveProfileName"><input ref="profileNameInput" v-model="profileName" maxlength="20" minlength="3" autocomplete="nickname" aria-label="Numele avatarului" /><button type="submit" :disabled="savingName" aria-label="Salvează numele">✓</button><button type="button" :disabled="savingName" aria-label="Renunță" @click="cancelNameEdit">×</button></form>
          <small v-if="nameError" class="name-error">{{ nameError }}</small><p class="created-label">{{ createdLabel }}</p><p class="exact-date">Din {{ exactCreated }}</p><button v-if="isOwnProfile" class="skin-editor-toggle" type="button" @click="skinPickerOpen = !skinPickerOpen">✦ {{ skinPickerOpen ? 'Închide ținutele' : 'Schimbă ținuta' }}</button></div>
        <section v-if="!isOwnProfile" class="relationship-panel" aria-label="Relația cu acest avatar">
          <template v-if="session?.isGuest"><strong>Relațiile se salvează într-un cont</strong><p>Creează un cont ca să poți adăuga prieteni.</p></template>
          <template v-else-if="selected.userId.startsWith('guest:')"><strong>Avatar temporar</strong><p>Poți adăuga relații doar cu persoane care au cont.</p></template>
          <template v-else>
            <div class="relationship-heading"><div><small>RELAȚIA VOASTRĂ</small><strong>{{ relationshipLoading ? 'Verificăm…' : relationshipStatus }}</strong></div><span v-if="relationship" class="relationship-badge">✓</span></div>
            <div class="relationship-options">
              <button v-for="option in relationshipOptions" :key="option.id" type="button" :class="{ active: relationship === option.id }" :disabled="relationshipLoading || relationshipSaving" @click="chooseRelationship(option.id)"><span>{{ option.icon }}</span>{{ option.label }}</button>
            </div>
            <button v-if="relationship" class="relationship-remove" type="button" :disabled="relationshipSaving" @click="chooseRelationship(null)">Elimină relația</button>
            <small v-if="relationshipError" class="relationship-error">{{ relationshipError }}</small>
          </template>
        </section>
        <div v-if="isOwnProfile && skinPickerOpen && session" class="skin-picker">
          <p>Alege un look — se vede imediat în piațetă.</p>
          <div class="skin-picker-grid">
            <button v-for="outfit in outfitsFor(session.avatarType)" :key="outfit.id" type="button" :class="['skin-choice', { selected: outfitFromParts(session.avatarType, session.avatarParts) === outfit.id }]" :disabled="savingSkin" @click="chooseSkin(outfit.id)">
              <span class="outfit-preview" :style="outfitPreviewStyle(outfit.id)"></span><small>{{ outfit.label }}</small>
            </button>
          </div>
          <small v-if="savingSkin" class="skin-status">Salvăm ținuta…</small><small v-else-if="skinError" class="skin-error">{{ skinError }}</small>
        </div>
      </aside>
    </div>
    <div v-if="maintenanceNotice" class="update-backdrop" role="alertdialog" aria-modal="true" aria-label="Actualizare în curs" @pointerdown.stop>
      <section class="update-modal">
        <span class="update-spinner" aria-hidden="true"></span>
        <p class="profile-kicker">UPDATE ÎN CURS</p>
        <h2>Facem lumea puțin mai bună.</h2>
        <p>{{ maintenanceNotice.message }}</p>
        <small>Rămâi aici — revenim automat.</small>
      </section>
    </div>
  </main>
</template>
